/**
 * AgentOsWorkspaceDashboard.jsx
 *
 * Post-onboarding personalized dashboard at /agent-os/workspace/:workspaceId.
 * This is the MOMENT-OF-TRUTH page for trial → paid conversion — every
 * fresh trial user lands here after the wizard's "Otvoriť dashboard" CTA.
 *
 * Sections (top-to-bottom):
 *   1. Welcome header (brand color + brand_name + primary_agent)
 *   2. Trial countdown banner (days_left + upgrade CTA when ≤3)
 *   3. 3-step onboarding checklist (interactive — POST to update)
 *   4. First Win tracker (replies_generated × eur_saved)
 *   5. Add Another Agent CTA (Bundle 3 za 2)
 *
 * Reads workspace + stats via /api/agent-os/onboarding/workspace/{id}/stats.
 * No auth required (yet) — workspace id is treated as a soft secret in
 * the URL. Future: replace with magic-link email auth.
 */
import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { API } from "../config";
import {
  Bot, ArrowRight, CheckCircle, Circle, Loader2, Star, Clock,
  TrendingUp, Sparkles, Link as LinkIcon, ShieldCheck, Plus,
  Bell, BellOff, RefreshCw, Users, Eye, Coins,
} from "lucide-react";
import { AchievementPanel, SmallShareWinButton } from "../components/agent-os/Achievements";
import GmailHelpdeskCard from "../components/agent-os/GmailHelpdeskCard";
import TrialProgressBar from "../components/agent-os/TrialProgressBar";
import ShareForCreditsCard from "../components/agent-os/ShareForCreditsCard";
import EshopConnectionsCard from "../components/agent-os/EshopConnectionsCard";
import AchievementsBadgeWall from "../components/agent-os/AchievementsBadgeWall";
import MyAgentsStatsWidget from "../components/dashboard/MyAgentsStatsWidget";

const AGENT_LABEL = {
  review_reply:  "Review Reply Agent",
  cart_recovery: "Cart Recovery Agent",
  newsletter:    "Newsletter Marketing Agent",
  product_desc:  "Product Desc Optimizer",
  gdpr_watch:    "GDPR + A11y Watch",
  helpdesk:      "Helpdesk Agent",
  social_media:  "Social Media Scheduler",
  pricing_intel: "Pricing Intelligence",
  customer_onboarding: "Customer Onboarding Bot",
  review_moderator: "Review Moderator",
  behavior_analytics: "Behavior Analytics",
};

// Normalize the workspace's brand color to a safe 6-digit hex so the
// inline gradient styles below (which append a hex alpha "15") don't
// silently break when a 3-digit or non-hex value sneaks through.
function sanitizeColor(input) {
  if (!input || typeof input !== "string") return null;
  const m = input.trim().match(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
  if (!m) return null;
  const hex = m[1];
  return hex.length === 3
    ? `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`
    : `#${hex.toLowerCase()}`;
}

export default function AgentOsWorkspaceDashboard() {
  const params = useParams();
  const [search] = useSearchParams();
  const workspaceId = params.workspaceId || search.get("workspace");

  const [data, setData] = useState(null);
  const [status, setStatus] = useState({ loading: true, error: null });

  // Live-update guards. We compare every poll's replies_generated to
  // the previous snapshot; on increment we fire a toast + (if opted-in)
  // a desktop notification. The first-ever poll seeds the baseline and
  // never fires — that's the initial render, not a "new" win.
  const prevRepliesRef = useRef(null);
  const [notifPermission, setNotifPermission] = useState(
    typeof window !== "undefined" && "Notification" in window
      ? Notification.permission
      : "unsupported",
  );

  const fireWinToast = (delta, accent) => {
    toast.success(
      delta === 1
        ? "🎉 Tvoj prvý win!"
        : `🚀 +${delta} nových AI odpovedí`,
      {
        description: delta === 1
          ? "Agent práve dokončil prvú odpoveď. Sleduj First Win tracker."
          : "First Win tracker práve narástol.",
        duration: 6000,
        style: accent ? { borderLeft: `4px solid ${accent}` } : undefined,
      },
    );
    if (typeof window !== "undefined" && "Notification" in window
        && Notification.permission === "granted") {
      try {
        const n = new Notification("Agent OS — nový win 🎯", {
          body: delta === 1
            ? "Tvoj prvý AI win je tu!"
            : `+${delta} nových AI odpovedí pre tvoj shop.`,
          tag: "agent-os-win",
        });
        // Auto-close after 8s to avoid pinned-notification clutter.
        setTimeout(() => { try { n.close(); } catch { /* */ } }, 8000);
      } catch { /* permission edge cases — silent */ }
    }
  };

  const refresh = async (opts = {}) => {
    if (!workspaceId) {
      setStatus({ loading: false, error: "Chýba workspace id v URL." });
      return;
    }
    try {
      const r = await axios.get(`${API}/agent-os/onboarding/workspace/${workspaceId}/stats`);
      const fresh = r.data;
      // Win detection — only when we already have a baseline snapshot
      // AND the new value is higher. Skip silent mode (eg. user clicked
      // a checklist item which triggers refresh but isn't a "win").
      const prev = prevRepliesRef.current;
      const next = fresh?.stats?.replies_generated ?? 0;
      if (!opts.silent && prev !== null && next > prev) {
        fireWinToast(next - prev, fresh?.brand_color);
      }
      prevRepliesRef.current = next;

      // Badge unlock celebrations — fired only for fresh persistence
      // (the BE returns `new_unlocks` per call). One toast per badge
      // per session, since the BE only flags fresh additions to the
      // workspace.badges_unlocked array.
      if (!opts.silent && Array.isArray(fresh?.new_unlocks) && fresh.new_unlocks.length > 0) {
        fresh.new_unlocks.forEach((bid) => {
          const b = (fresh.badges || []).find((x) => x.id === bid);
          if (b) {
            toast.success(`${b.emoji} Práve si odomkol "${b.name}"!`, {
              description: b.desc,
              duration: 8000,
              style: { borderLeft: `4px solid ${fresh.brand_color || "#34d399"}` },
            });
          }
        });
      }
      setData(fresh);
      setStatus({ loading: false, error: null });
    } catch (e) {
      setStatus({
        loading: false,
        error: e?.response?.status === 404 ? "Workspace nenájdený." : "Skús to obnoviť.",
      });
    }
  };

  // Initial fetch + 30s polling. Pauses while the tab is hidden so we
  // don't burn API calls in background tabs (Page Visibility API).
  useEffect(() => {
    let alive = true;
    refresh({ silent: true });
    let intervalId = null;

    const startPolling = () => {
      if (intervalId) return;
      intervalId = setInterval(() => { if (alive) refresh(); }, 30000);
    };
    const stopPolling = () => {
      if (intervalId) { clearInterval(intervalId); intervalId = null; }
    };
    const onVisibility = () => {
      if (document.hidden) stopPolling();
      else { refresh({ silent: true }); startPolling(); }
    };

    startPolling();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      alive = false;
      stopPolling();
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const requestNotifications = async () => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission === "granted") {
      setNotifPermission("granted");
      return;
    }
    try {
      const perm = await Notification.requestPermission();
      setNotifPermission(perm);
      if (perm === "granted") {
        toast.success("Notifikácie aktivované 🔔", {
          description: "Dáme ti vedieť keď agent dokončí prvý win.",
        });
      }
    } catch { /* user blocked browser permission */ }
  };

  if (status.loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center" data-testid="agent-os-workspace-loading">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4 text-emerald-400" />
          <p className="text-sm text-gray-500 uppercase tracking-widest">Načítavam dashboard…</p>
        </div>
      </div>
    );
  }

  if (status.error || !data) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-6" data-testid="agent-os-workspace-error">
        <div className="text-center max-w-md">
          <h1 className="text-3xl font-black mb-3">Hups, niečo zlyhalo</h1>
          <p className="text-base text-gray-400 mb-6">{status.error}</p>
          <a href="/agent-os/onboard" className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-400 hover:bg-emerald-300 text-black font-black uppercase tracking-widest text-sm">
            Spustiť onboarding <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    );
  }

  const accent = sanitizeColor(data.brand_color) || "#34d399";
  const accentBg = { backgroundColor: accent };
  const accentText = { color: accent };
  const accentBorder = { borderColor: accent + "60" };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="agent-os-workspace-dashboard">
      <Toaster
        position="top-right"
        theme="dark"
        toastOptions={{
          style: { background: "#0a0a0a", border: "1px solid #262626", color: "#fff" },
        }}
      />
      <TopBar
        accent={accent}
        brandName={data.brand_name}
        notifPermission={notifPermission}
        onRequestNotifications={requestNotifications}
        onManualRefresh={() => refresh({ silent: true })}
      />
      <main className="max-w-5xl mx-auto px-6 py-10">
        <WelcomeHeader data={data} accent={accent} />
        <TrialProgressBar workspaceId={workspaceId} accent={accent} />
        <AchievementsBadgeWall workspaceId={workspaceId} accent={accent} />
        <ShareForCreditsCard workspaceId={workspaceId} accent={accent} />
        <TrialBanner data={data} accent={accent} />
        <Checklist data={data} workspaceId={workspaceId} accent={accent} accentBg={accentBg} accentText={accentText} onRefresh={() => refresh({ silent: true })} />
        <FirstWinTracker data={data} workspaceId={workspaceId} accent={accent} accentText={accentText} accentBorder={accentBorder} />
        <div className="mt-4">
          <MyAgentsStatsWidget workspaceId={workspaceId} days={30} />
        </div>
        <ReferralsCard workspaceId={workspaceId} accent={accent} accentText={accentText} accentBorder={accentBorder} />
        <GmailHelpdeskCard workspaceId={workspaceId} accent={accent} accentBg={accentBg} accentBorder={accentBorder} />
        <EshopConnectionsCard workspaceId={workspaceId} accent={accent} />
        <AvailableAgents primaryAgent={data.primary_agent} accent={accent} accentBg={accentBg} accentBorder={accentBorder} />
        <AchievementPanel badges={data.badges || []} workspaceId={workspaceId} accent={accent} />
        <AddAgentCta accent={accent} accentBg={accentBg} />
      </main>
      <footer className="border-t border-gray-900 py-6 text-center text-xs text-gray-600">
        TopRankPulse · Agent OS · workspace <code className="text-gray-500">{data.workspace_id}</code>
        <span className="mx-2">·</span>
        <span data-testid="agent-os-workspace-polling-indicator" className="text-emerald-500/70">
          ● auto-refresh každých 30 s
        </span>
      </footer>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function TopBar({ accent, brandName, notifPermission, onRequestNotifications, onManualRefresh }) {
  const canPrompt = notifPermission === "default";
  const isGranted = notifPermission === "granted";
  return (
    <header className="border-b border-gray-900 bg-black/80 backdrop-blur sticky top-0 z-30">
      <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
        <a href="/" className="flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center" style={{ backgroundColor: accent }}>
            <Bot className="w-5 h-5 text-black" />
          </div>
          <span className="font-black tracking-tight">
            <span style={{ color: accent }}>{brandName || "Tvoj brand"}</span>
            <span className="text-gray-600 mx-2">·</span>
            <span className="text-gray-400">Agent OS</span>
          </span>
        </a>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onManualRefresh}
            data-testid="agent-os-workspace-manual-refresh"
            title="Obnoviť teraz"
            aria-label="Obnoviť"
            className="p-1.5 text-gray-400 hover:text-white"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          {canPrompt && (
            <button
              type="button"
              onClick={onRequestNotifications}
              data-testid="agent-os-workspace-notif-enable"
              className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-700 hover:border-white text-white text-[10px] font-bold uppercase tracking-widest"
            >
              <Bell className="w-3 h-3" /> Zapnúť notifikácie
            </button>
          )}
          {isGranted && (
            <span
              data-testid="agent-os-workspace-notif-on"
              className="inline-flex items-center gap-1 px-3 py-1.5 border text-[10px] font-bold uppercase tracking-widest"
              style={{ color: accent, borderColor: accent + "60" }}
            >
              <Bell className="w-3 h-3" /> Notifikácie zapnuté
            </span>
          )}
          {notifPermission === "denied" && (
            <span
              data-testid="agent-os-workspace-notif-blocked"
              className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-800 text-gray-500 text-[10px] font-bold uppercase tracking-widest"
            >
              <BellOff className="w-3 h-3" /> Notifikácie blokované
            </span>
          )}
          <a
            href="/agent-os/eshop#agent-picker"
            data-testid="agent-os-workspace-topbar-cta"
            className="px-3 py-1.5 border border-gray-700 hover:border-white text-white text-xs font-bold uppercase tracking-widest"
          >
            Marketplace
          </a>
        </div>
      </div>
    </header>
  );
}

/* ------------------------------------------------------------------ */

function WelcomeHeader({ data, accent }) {
  return (
    <section className="mb-8" data-testid="agent-os-workspace-welcome">
      <div className="text-xs uppercase tracking-[0.3em] font-bold mb-3" style={{ color: accent }}>
        Tvoj workspace
      </div>
      <h1 className="text-4xl sm:text-5xl font-black mb-3">
        Vitaj späť, <span style={{ color: accent }}>{data.brand_name || "shop"}</span> 👋
      </h1>
      <p className="text-base text-gray-400 max-w-xl">
        Tvoj <strong className="text-white">{AGENT_LABEL[data.primary_agent] || data.primary_agent}</strong> je live.
        Tu je čo môžeš dnes urobiť, aby si vyťažil maximum z trialu.
      </p>
    </section>
  );
}

/* ------------------------------------------------------------------ */

function TrialBanner({ data, accent }) {
  const days = data.days_left ?? 0;
  const urgent = days <= 3;
  const expired = days < 0;
  return (
    <section
      data-testid="agent-os-workspace-trial-banner"
      className={`p-5 mb-8 border-2 ${
        urgent ? "border-rose-700/60 bg-gradient-to-br from-rose-950/40 to-black" : "border-gray-800 bg-black"
      }`}
    >
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Clock className={`w-6 h-6 ${urgent ? "text-rose-400" : "text-gray-500"}`} />
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-gray-500">Free trial</div>
            <div className={`text-xl font-black tabular-nums ${urgent ? "text-rose-300" : "text-white"}`} data-testid="agent-os-workspace-days-left">
              {expired
                ? "Trial vypršal"
                : days === 0
                ? "Posledný deň"
                : `${days} ${days === 1 ? "deň" : days < 5 ? "dni" : "dní"} zostáva`}
            </div>
          </div>
        </div>
        {(urgent || expired) && (
          <a
            href="/agent-os/eshop#pricing"
            data-testid="agent-os-workspace-upgrade-cta"
            className="px-5 py-2 text-black font-black uppercase tracking-widest text-xs"
            style={{ backgroundColor: accent }}
          >
            Upgradnuť teraz →
          </a>
        )}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */

function Checklist({ data, workspaceId, accent, accentBg, accentText, onRefresh }) {
  const items = [
    {
      key: "connected_shop",
      title: "Prepoj svoj e-shop",
      desc: "Heureka URL, WooCommerce token alebo Shopify OAuth",
      cta: "Pridať URL",
    },
    {
      key: "approved_first",
      title: "Schváľ prvé 3 AI odpovede",
      desc: "Agent sa učí z tvojho feedbacku — kvalita rastie každým schválením",
      cta: "Otvoriť approve queue",
    },
    {
      key: "second_agent",
      title: "Pridaj druhého agenta",
      desc: "Bundle 3 za 2 — tretí agent je zadarmo (€198 namiesto €297)",
      cta: "Otvoriť marketplace",
    },
  ];

  const progress = data.checklist_completed / Math.max(1, data.checklist_total);

  return (
    <section className="mb-8" data-testid="agent-os-workspace-checklist">
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] font-bold mb-2" style={accentText}>
            Onboarding · {data.checklist_completed}/{data.checklist_total}
          </div>
          <h2 className="text-2xl font-black">Aktivuj plnú silu Agent OS</h2>
        </div>
        <div className="w-32 h-2 bg-gray-900 overflow-hidden">
          <div
            className="h-full transition-all"
            style={{ width: `${progress * 100}%`, backgroundColor: accent }}
            data-testid="agent-os-workspace-checklist-progress"
          />
        </div>
      </div>

      <div className="space-y-3">
        {items.map((it) => (
          <ChecklistItem
            key={it.key}
            item={it}
            done={data.checklist[it.key]}
            workspaceId={workspaceId}
            accent={accent}
            accentBg={accentBg}
            onRefresh={onRefresh}
          />
        ))}
      </div>
    </section>
  );
}

function ChecklistItem({ item, done, workspaceId, accent, accentBg, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [shopUrl, setShopUrl] = useState("");

  const markDone = async (extra = {}) => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/agent-os/onboarding/workspace/${workspaceId}/checklist`,
        { step: item.key, value: true, ...extra },
      );
      onRefresh();
    } finally {
      setBusy(false);
    }
  };

  const handleCta = () => {
    if (item.key === "connected_shop") {
      setShowInput((s) => !s);
    } else {
      markDone();
    }
  };

  return (
    <div
      data-testid={`agent-os-workspace-checklist-${item.key}`}
      className={`p-4 border ${done ? "border-gray-800 bg-black/40 opacity-60" : "border-gray-800 bg-black hover:border-gray-700 transition-colors"}`}
    >
      <div className="flex items-start gap-4">
        <div className="mt-0.5 shrink-0">
          {done
            ? <CheckCircle className="w-6 h-6" style={{ color: accent }} />
            : <Circle className="w-6 h-6 text-gray-700" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h3 className={`text-base font-black ${done ? "line-through text-gray-500" : "text-white"}`}>{item.title}</h3>
              <p className="text-xs text-gray-400 mt-0.5">{item.desc}</p>
            </div>
            {!done && (
              <button
                type="button"
                onClick={handleCta}
                disabled={busy}
                data-testid={`agent-os-workspace-checklist-${item.key}-cta`}
                className="px-4 py-2 text-black font-black uppercase tracking-widest text-[10px] whitespace-nowrap disabled:opacity-50"
                style={accentBg}
              >
                {busy ? "Pracujem…" : item.cta}
              </button>
            )}
          </div>

          {!done && showInput && item.key === "connected_shop" && (
            <div className="mt-3 flex gap-2" data-testid="agent-os-workspace-shop-input-wrap">
              <input
                type="url"
                placeholder="https://môj-shop.sk"
                value={shopUrl}
                onChange={(e) => setShopUrl(e.target.value)}
                data-testid="agent-os-workspace-shop-input"
                className="flex-1 px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:outline-none text-white placeholder-gray-600"
                style={shopUrl ? { borderColor: accent } : undefined}
              />
              <button
                type="button"
                onClick={() => shopUrl && markDone({ shop_url: shopUrl })}
                disabled={busy || !shopUrl}
                data-testid="agent-os-workspace-shop-save"
                className="px-3 py-2 text-black font-black uppercase tracking-widest text-[10px] disabled:opacity-40"
                style={accentBg}
              >
                Uložiť
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function FirstWinTracker({ data, workspaceId, accent, accentText, accentBorder }) {
  const s = data.stats || {};
  const empty = (s.replies_generated || 0) === 0;

  return (
    <section className="mb-8" data-testid="agent-os-workspace-firstwin">
      <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] font-bold mb-2" style={accentText}>
            First Win Tracker
          </div>
          <h2 className="text-2xl font-black">Koľko ti agent zatiaľ ušetril</h2>
        </div>
        <SmallShareWinButton
          workspaceId={workspaceId}
          accent={accent}
          repliesGenerated={s.replies_generated || 0}
        />
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <MetricCard
          label="AI odpovedí"
          value={(s.replies_generated || 0).toLocaleString("sk-SK")}
          icon={<Star className="w-5 h-5" />}
          accent={accent}
          accentBorder={accentBorder}
          testid="agent-os-workspace-metric-replies"
        />
        <MetricCard
          label="Ušetrené minúty"
          value={(s.estimated_minutes_saved || 0).toLocaleString("sk-SK")}
          icon={<Clock className="w-5 h-5" />}
          accent={accent}
          accentBorder={accentBorder}
          testid="agent-os-workspace-metric-minutes"
        />
        <MetricCard
          label="Ušetrené €"
          value={`€${(s.estimated_eur_saved || 0).toLocaleString("sk-SK")}`}
          icon={<TrendingUp className="w-5 h-5" />}
          accent={accent}
          accentBorder={accentBorder}
          testid="agent-os-workspace-metric-eur"
        />
      </div>

      {empty && (
        <p className="mt-4 text-xs text-gray-500 italic" data-testid="agent-os-workspace-firstwin-empty">
          Číslo skočí akonáhle prepojíš shop a agent začne odpovedať. Zvyčajne do 24 hodín.
        </p>
      )}
    </section>
  );
}

function MetricCard({ label, value, icon, accent, accentBorder, testid }) {
  return (
    <div
      data-testid={testid}
      className="p-5 bg-black border-2 transition-colors hover:translate-y-[-2px]"
      style={accentBorder}
    >
      <div className="flex items-center gap-2 mb-3" style={{ color: accent }}>
        {icon}
        <span className="text-[10px] uppercase tracking-widest font-bold">{label}</span>
      </div>
      <div className="text-4xl font-black tabular-nums" style={{ color: accent }}>{value}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

/**
 * ReferralsCard — surfaces the viral-loop KPIs for this workspace:
 * how many shares the user has created, their combined view count,
 * how many of those views actually converted into trial signups,
 * and the estimated commission (€99 MRR × 30% × signups).
 *
 * Empty state — when the user hasn't shared yet — shows a low-friction
 * nudge ("Vyhraj badge → zdieľaj → zarob") to push them back up into
 * the Achievement panel above. Real money is the strongest motivator
 * here, so the commission card is amber/bold, not muted.
 */
function ReferralsCard({ workspaceId, accent, accentText, accentBorder }) {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/agent-os/onboarding/workspace/${workspaceId}/referrals`);
        if (!cancelled) setData(r.data);
      } catch { /* silent — card just stays hidden if it 404s */ }
      finally { if (!cancelled) setLoaded(true); }
    })();
    return () => { cancelled = true; };
  }, [workspaceId]);

  if (!loaded) return null;
  if (!data) return null;

  const hasShared = (data.share_count || 0) > 0;
  const hasSignup = (data.referral_signups || 0) > 0;
  const rank = data.rank;
  const rankPool = data.rank_pool_size || 0;

  return (
    <section
      className="mb-8"
      data-testid="agent-os-workspace-referrals"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xs font-black uppercase tracking-[0.2em] flex items-center gap-2 text-gray-400">
          <Users className="w-3.5 h-3.5" /> Tvoji referrals
        </h2>
        {hasShared && (
          <span className="text-[10px] uppercase tracking-widest text-gray-500">
            30% lifetime commission
          </span>
        )}
      </div>

      {/* Leaderboard rank chip — only when the workspace has at
          least one converted signup. Top 3 get amber medal styling,
          #4+ stays accent-colored. Clicks through to public board. */}
      {rank && (
        <a
          href="/agent-os/leaderboard"
          data-testid="agent-os-referrals-rank-chip"
          className={`mb-4 flex items-center justify-between gap-3 p-4 border-2 transition-colors hover:translate-y-[-1px] ${
            rank <= 3
              ? "bg-gradient-to-r from-amber-950/40 to-black border-amber-500/60"
              : "bg-black border-gray-800 hover:border-emerald-700/60"
          }`}
        >
          <div className="flex items-center gap-3">
            <span className={`text-3xl font-black tabular-nums ${rank <= 3 ? "text-amber-300" : "text-emerald-400"}`}>
              {rank <= 3 ? ["🥇", "🥈", "🥉"][rank - 1] : `#${rank}`}
            </span>
            <div>
              <div className="text-sm font-black text-white">
                Si #{rank} v Top Referrers
              </div>
              <div className="text-xs text-gray-400">
                z {rankPool.toLocaleString("sk-SK")} aktívnych referrer-ov ·
                {rank === 1
                  ? " si #1! Drž si pozíciu."
                  : rank <= 3
                    ? " si na pódiu 🏆 ďalším signupom posunieš ostatných."
                    : ` posuň sa ${Math.min(3, rank - 1)} priečku vyššie pre 🥉 medailu.`}
              </div>
            </div>
          </div>
          <span className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold whitespace-nowrap">
            Otvor leaderboard →
          </span>
        </a>
      )}

      <div className="grid sm:grid-cols-3 gap-4 mb-4">
        <MetricCard
          label="Zobrazené share pages"
          value={(data.view_count || 0).toLocaleString("sk-SK")}
          icon={<Eye className="w-4 h-4" />}
          accent={accent}
          accentBorder={accentBorder}
          testid="agent-os-referrals-views"
        />
        <MetricCard
          label="Konvertované signupy"
          value={data.referral_signups || 0}
          icon={<Users className="w-4 h-4" />}
          accent={accent}
          accentBorder={accentBorder}
          testid="agent-os-referrals-signups"
        />
        <div
          data-testid="agent-os-referrals-commission"
          className="p-5 bg-black border-2 transition-colors hover:translate-y-[-2px]"
          style={{ borderColor: hasSignup ? "#fbbf24" : accent + "60" }}
        >
          <div
            className="flex items-center gap-2 mb-3"
            style={{ color: hasSignup ? "#fbbf24" : accent }}
          >
            <Coins className="w-4 h-4" />
            <span className="text-[10px] uppercase tracking-widest font-bold">
              Odhadovaná provízia
            </span>
          </div>
          <div
            className="text-4xl font-black tabular-nums"
            style={{ color: hasSignup ? "#fbbf24" : accent }}
          >
            €{(data.estimated_commission_eur || 0).toLocaleString("sk-SK", {
              minimumFractionDigits: 2, maximumFractionDigits: 2,
            })}
          </div>
        </div>
      </div>

      {data.recent && data.recent.length > 0 ? (
        <div className="bg-black border border-gray-800 p-4">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-3">
            Posledných {data.recent.length} signupov
          </div>
          <ul className="space-y-2">
            {data.recent.map((r, i) => (
              <li
                key={r.share_id + i}
                data-testid={`agent-os-referrals-recent-${i}`}
                className="flex items-center justify-between text-sm"
              >
                <span className="font-mono text-gray-300">{r.referee_email_initial}</span>
                <span className="text-xs text-gray-500">
                  {new Date(r.created_at).toLocaleDateString("sk-SK", {
                    day: "numeric", month: "short", year: "numeric",
                  })}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : !hasShared ? (
        <div
          className="bg-black p-5 border-2 border-dashed text-center"
          style={accentBorder}
          data-testid="agent-os-referrals-empty"
        >
          <p className="text-sm text-gray-300 mb-1">
            Ešte si nezdieľal žiadny badge.
          </p>
          <p className="text-xs text-gray-500">
            Odomkni achievement vyššie → klikni „Zdieľaj" → každý kto si u nás otvorí trial cez tvoj link ti prinesie <span style={accentText} className="font-bold">€29.70 / mes</span>.
          </p>
        </div>
      ) : (
        <div
          className="bg-black p-5 border border-gray-800 text-center text-xs text-gray-500"
          data-testid="agent-os-referrals-no-signups"
        >
          {data.share_count} share page{data.share_count === 1 ? "" : "s"} aktívnych · {data.view_count} zobrazení · ešte žiadny signup. Pridaj viac share pages alebo postni link na social media.
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */

/**
 * AvailableAgents — surfaces the rest of the Agent OS marketplace
 * inside the workspace. The user already has their `primary_agent`
 * activated (Welcome header), so this section lists the OTHER 4
 * agents with 1-click links to the demo sandbox. Push toward Bundle
 * 3-za-2 (handled by the AddAgentCta below).
 *
 * Why not duplicate AddAgentCta?
 *   AddAgentCta is a single-block "Marketplace →" CTA. This is the
 *   itemized version — user wants to see WHAT they're missing, not
 *   just a button to a marketplace.
 */
function AvailableAgents({ primaryAgent, accent, accentBorder }) {
  // Use the same source-of-truth list as HomeAgentMarketplaceStrip.
  // Hard-code here to keep workspace dashboard import surface small.
  const ALL = [
    { id: "helpdesk",      name: "Helpdesk",      saves: "Nahradí 1× CS agenta (€1 800)",  price: "€99/mes", demo: "/agent-os/demo/helpdesk" },
    { id: "review_reply",  name: "Review Reply",  saves: "30 min × 50 recenzií / mes",     price: "€79/mes", demo: "/agent-os/demo/review-reply" },
    { id: "cart_recovery", name: "Cart Recovery", saves: "12-18% recovery rate",            price: "€99/mes", demo: "/agent-os/demo/cart-recovery" },
    { id: "product_desc",  name: "Product Desc",  saves: "SEO + AI Search traffic",         price: "€79/mes", demo: "/agent-os/demo/product-desc" },
    { id: "newsletter",    name: "Newsletter",    saves: "1× SMM (€1 500)",                 price: "€79/mes", demo: "/agent-os/demo/newsletter" },
    { id: "gdpr_watch",    name: "GDPR + A11y Watch", saves: "Denný compliance scan",       price: "€49/mes", demo: "/agent-os/demo/gdpr-watch" },
    { id: "social_media",  name: "Social Media Scheduler", saves: "1× SMM junior (€1 000)", price: "€79/mes", demo: "/agent-os/demo/social-media" },
    { id: "pricing_intel", name: "Pricing Intelligence", saves: "5-12% margin lift",        price: "€149/mes", demo: "/agent-os/demo/pricing-intel" },
    { id: "customer_onboarding", name: "Customer Onboarding", saves: "+25% repeat purchase", price: "€69/mes", demo: "/agent-os/demo/customer-onboarding" },
    { id: "review_moderator", name: "Review Moderator", saves: "Detekuje fake recenzie",     price: "€69/mes", demo: "/agent-os/demo/review-moderator" },
    { id: "behavior_analytics", name: "Behavior Analytics", saves: "Týždenné AI návrhy opráv", price: "€129/mes", demo: "/agent-os/demo/behavior-analytics" },
  ];
  const others = ALL.filter((a) => a.id !== primaryAgent);
  if (others.length === 0) return null;

  return (
    <section className="mb-8" data-testid="agent-os-workspace-available-agents">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xs font-black uppercase tracking-[0.2em] flex items-center gap-2 text-gray-400">
          <Plus className="w-3.5 h-3.5" /> Pridaj ďalšieho agenta
        </h2>
        <span className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">
          Bundle 3-za-2 · ušetri €99/mes
        </span>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {others.map((a) => (
          <a
            key={a.id}
            href={a.demo || "/agent-os/eshop"}
            data-testid={`agent-os-available-${a.id}`}
            className="group p-4 bg-black border transition-colors hover:translate-y-[-2px]"
            style={accentBorder}
          >
            <div className="flex items-start justify-between mb-2">
              <span className="text-sm font-black text-white">{a.name}</span>
              <span className="text-[10px] font-black tabular-nums" style={{ color: accent }}>
                {a.price}
              </span>
            </div>
            <p className="text-[11px] text-gray-500 mb-2 leading-snug">{a.saves}</p>
            <span
              className="text-[10px] uppercase tracking-widest font-bold flex items-center gap-1"
              style={{ color: accent }}
            >
              {a.demo ? "🎬 Skús demo →" : "Onedlho"}
            </span>
          </a>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */

function AddAgentCta({ accent, accentBg }) {
  return (
    <section
      data-testid="agent-os-workspace-add-agent"
      className="p-8 border-2 text-center"
      style={{ borderColor: accent + "60", background: `linear-gradient(135deg, ${accent}15, #0a0a0a)` }}
    >
      <Plus className="w-10 h-10 mx-auto mb-3" style={{ color: accent }} />
      <h2 className="text-2xl sm:text-3xl font-black mb-3">
        Pridaj druhého agenta. <span style={{ color: accent }}>Tretí zadarmo.</span>
      </h2>
      <p className="text-sm text-gray-400 mb-6 max-w-xl mx-auto">
        Bundle 3 za 2 — €198/mes namiesto €297. Helpdesk + Cart Recovery + Newsletter sa navzájom dopĺňajú a ušetria ti dvakrát toľko.
      </p>
      <a
        href="/agent-os/eshop#agent-picker"
        data-testid="agent-os-workspace-bundle-cta"
        className="inline-flex items-center gap-2 px-7 py-3 text-black font-black uppercase tracking-widest text-sm"
        style={accentBg}
      >
        Otvoriť marketplace <ArrowRight className="w-4 h-4" />
      </a>
    </section>
  );
}
